import './ResetPassword.css';
import '../common-style.css';

import { useContext, useState } from 'react';
import { createOnChangeHandler, isAnyFalsish, validateEmail, validateInput } from 't-lib-ui/helpers';
import { resetPassword } from '../../connector/auth';
import { FetchError } from 't-lib-ui/connector';
import { validatePassword } from '../../schema-copy/security';
import { EmailLength, PasswordLength } from '../../schema-copy/auth-schema';
import { useProgressModal } from 't-lib-ui/structure';
import { redirect, RouterContext, getLocation } from 't-lib-ui/router';
import { FlowForm, useFlowFormGlobal } from 't-lib-ui/structure';
import { Input, PasswordInput } from 't-lib-ui/widgets';
import { TextButton } from 't-lib-ui/widgets';
import { FormGrid } from 't-lib-ui/structure';
import { Link } from 't-lib-ui/router';
import { FormGridLine } from 't-lib-ui/structure';
import { ErrorNote } from 't-lib-ui/widgets';
import DownreadLogo from '../../components/DownreadLogo';


const emailErrorTexts = {
  missing: 'Please enter your email here.',
  bad: 'The email you have entered is not valid.'
};

const codeErrorTexts = {
  missing: 'Please enter the code from the email.',
  wrong: 'This code is not correct. Please check the code from the email.'
};

const passwordErrorTexts = {
  missing: 'Please enter a password.',
  short: 'Too short. The password needs to be at least 8 characters long.',
  simple: 'Too simple. Use upper and lower case, digits and/or special characters.'
};

const repeatPasswordErrorTexts = {
  missing: 'Please repeat the password here.',
  unequal: 'Both passwords must match.'
};

const statusErrorMessages = {
  connection: <>Failed to connect to server. Please try again later.</>,
  wrongCode: <>That reset code is not correct. Please check the code from the email and try again.</>,
  invalidToken: <>The reset code or link you used appears to be incorrect or outdated.</>,
  badPassword: <>Your password was not accepted by the server. Please try a more complex password.</>
};


const Page: React.FC = () => {
  const routerCtx = useContext(RouterContext);
  useFlowFormGlobal();
  const [presentLoading, dismissLoading] = useProgressModal();

  const location = getLocation(routerCtx);
  const urlUserId = location.searchParams.get('userId');
  const urlCode = location.searchParams.get('code');

  // Link mode: identity (userId) and code come from the email link's query params.
  // Manual mode: the link wasn't used, so the user types their email and the code from the email.
  const manualMode = !urlUserId || !urlCode;

  const [email, setEmail] = useState(location.searchParams.get('email') ?? '');
  const [emailError, setEmailError] = useState<'missing'|'bad'|undefined>(undefined);

  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<'missing'|'wrong'|undefined>(undefined);

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<'missing'|'short'|'simple'|undefined>(undefined);

  const [repeatPassword, setRepeatPassword] = useState('');
  const [repeatPasswordError, setRepeatPasswordError] = useState<'unequal'|'missing'|undefined>(undefined);

  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState<'ok'|'done'|'working'|'badPassword'|'wrongCode'|'invalidToken'|'fields'|'connection'>('ok');

  const validationNeededChecker = () => submitted;

  const statusFieldError = status == 'fields' && (!!emailError || !!codeError || !!passwordError || !!repeatPasswordError);

  function validateCode(value: string): 'missing'|undefined {
    return value.trim() ? undefined : 'missing';
  }

  function validateRepeatPassword(value: string): 'missing'|'unequal'|undefined {
    if (!value)
      return 'missing';
    return password != value ? 'unequal' : undefined;
  }

  function validatePasswordSignup(value: string): 'missing'|'short'|'simple'|undefined {
    if (!value.trim())
      return 'missing';
    const errors = validatePassword('', value);
    if (!errors.length)
      return undefined;

    if (['passwordNoWhitespace', 'passwordShort', 'passwordLong'].includes(errors[0]))
      return 'short';
    return 'simple';
  }


  const resetClicked = (): boolean => {
    setSubmitted(true);
    const passwordsValid = !isAnyFalsish(validateInput(password, validatePasswordSignup, setPasswordError),
        validateInput(repeatPassword, validateRepeatPassword, setRepeatPasswordError));
    const credentialsValid = !manualMode ||
        !isAnyFalsish(validateInput(email, validateEmail, setEmailError),
            validateInput(code, validateCode, setCodeError));
    if (!passwordsValid || !credentialsValid) {
      setStatus('fields');
      return true;
    }
    setStatus('working');
    presentLoading({
      message: 'Resetting password...',
      timeout: 5000
    });
    const identity = manualMode ? { email } : { id: urlUserId! };
    const token = manualMode ? code : urlCode!;
    resetPassword(identity, token, password).then(() => {
      dismissLoading();
      setStatus('done');
    }).catch((e) => {
      const type = e instanceof FetchError ? e.type : 'Connection';
      // A wrong reset code comes back as LoginFailed (the token exists but didn't match) — that's a
      // code problem, not a password problem. TokenInvalid means the token is expired/blocked/gone.
      // Only AccountUpdateError is an actual server-side rejection of the new password.
      if (type == 'LoginFailed') {
        setStatus('wrongCode');
        setCodeError('wrong');
      }
      else if (type == 'TokenInvalid')
        setStatus('invalidToken');
      else if (type == 'AccountUpdateError') {
        setStatus('badPassword');
        setPasswordError('simple');
      }
      else
        setStatus('connection');
      dismissLoading();
    });
    return true;
  };

  if (status == 'done')
    return redirect(routerCtx, '/login');

  return (
    <FlowForm main={true}>
      <div className="authPage">
        <div className="authCard resetCard">
          <div className="authLogo">
            <a href="https://downread.com" className="authLogoLink"><DownreadLogo size={48} showText /></a>
          </div>
          <div className="authIntro">Set a new password for your account.</div>
          <ErrorNote errorId={status} errorTexts={statusErrorMessages} />

          <div className='resetInfo'>{manualMode
            ? 'Enter your email and the code from the password reset email, then choose a new password.'
            : 'Please enter your new password twice.'}</div>
          <FormGrid>
            {manualMode ? <>
              <FormGridLine span={2} className='resetInputRow'>
                <Input label='Email' type="email" layout='singleCol' className='half-margin-bottom' value={email} errorId={emailError} errorTexts={emailErrorTexts} enterKeyHint="next" maxLength={EmailLength} onInput={createOnChangeHandler(setEmail, validateEmail, setEmailError, validationNeededChecker)} />
              </FormGridLine>
              <FormGridLine span={2} className='resetInputRow'>
                <Input label='Reset code' layout='singleCol' autoComplete='one-time-code' className='half-margin-bottom' value={code} errorId={codeError} errorTexts={codeErrorTexts} enterKeyHint="next" maxLength={32} onInput={createOnChangeHandler(setCode, validateCode, setCodeError, validationNeededChecker)} />
              </FormGridLine>
            </> : undefined}
            <FormGridLine span={2} className='resetInputRow'>
              <PasswordInput label='Password' layout='singleCol' className='half-margin-bottom' errorId={passwordError} errorTexts={passwordErrorTexts} enterKeyHint="next" maxLength={PasswordLength} onInput={createOnChangeHandler(setPassword, validatePasswordSignup, setPasswordError, validationNeededChecker)} />
            </FormGridLine>
            <FormGridLine span={2} className='resetInputRow'>
              <PasswordInput label='Repeat Password' layout='singleCol' className='half-margin-bottom' errorId={repeatPasswordError} errorTexts={repeatPasswordErrorTexts} enterKeyHint="enter" maxLength={PasswordLength} onInput={createOnChangeHandler(setRepeatPassword, validateRepeatPassword, setRepeatPasswordError, validationNeededChecker)} />
            </FormGridLine>
            <FormGridLine span={2} align='center' className='resetSubmitRow'>
              <TextButton onClick={resetClicked} enabled={statusFieldError || status == 'working' ? 'disabled' : 'enabled'} label='Reset Password'  type='major' />
            </FormGridLine>
          </FormGrid>
          <div className="authLink">Didn&apos;t get a code? <Link to='/forgot-password'>Request a new one</Link></div>
        </div>
      </div>
    </FlowForm>
  );
};

export default Page;
